import {
  Button,
  Modal,
  Label,
  InputText,
  ErrorMessage,
  InputTextarea,
} from '@teo/components';
import { Warning } from '@teo/components/icons';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import ColorPicker from '@/components/ColorPicker';
import { config } from '@/config';
import { axios } from '@/axios';
import { newUid } from '@/util';

const AddCategoryModal = ({
  closeModal,
  setIsOpenAddModal,
  setRefetchTable,
  authUser,
  activeCategory = null,
  edit = false,
  refetchListCategories,
}) => {
  const { t } = useTranslation();
  const [checkValid, setCheckValid] = useState(false);
  const [nameCategory, setNameCategory] = useState(activeCategory?.name || '');
  const [codePrefix, setCodePrefix] = useState(
    activeCategory?.codePrefix || ''
  );
  const [descriptionCat, setDescriptionCat] = useState(
    activeCategory?.description || ''
  );
  const [colorPicker, setColorPicker] = useState(
    activeCategory?.colorCode || '#009e7a'
  );
  const [serverError, setServerError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const getConflictField = (error) => {
    const response = error?.response?.data;
    const errors = Array.isArray(response?.errors) ? response.errors : [];

    if (response?.code === 'CATEGORY_NAME_CONFLICT') return 'nameConflict';
    if (response?.code === 'CATEGORY_CODE_CONFLICT') return 'codeConflict';

    for (const item of errors) {
      const field = String(item?.field || '').toLowerCase();
      const message = String(item?.message || '').toLowerCase();

      if (field.includes('name') || message.includes('name must be unique')) {
        return 'nameConflict';
      }
      if (
        field.includes('codeprefix') ||
        message.includes('codeprefix must be unique')
      ) {
        return 'codeConflict';
      }
    }

    return '';
  };

  const handleSaveCategory = async () => {
    setCheckValid(true);
    setServerError('');

    if (!nameCategory || !codePrefix) return;

    setIsSaving(true);

    try {
      if (edit) {
        const changes = {};

        if (nameCategory !== activeCategory?.name) {
          changes.name = nameCategory;
        }
        if (codePrefix !== activeCategory?.codePrefix) {
          changes.codePrefix = codePrefix;
        }

        if (descriptionCat !== activeCategory?.description) {
          changes.description = descriptionCat;
        }
        if (colorPicker !== activeCategory?.colorCode) {
          changes.colorCode = colorPicker;
        }
        await axios.put(
          `${config.backend}/competencecategory/${activeCategory?.id}`,
          { ...changes }
        );
      } else {
        await axios.post(`${config.backend}/competencecategories`, {
          name: nameCategory,
          organisationId: authUser?.activeOrganisationId,
          lang: authUser?.lang,
          codePrefix: codePrefix,
          colorCode: colorPicker,
          description: descriptionCat,
        });
        refetchListCategories();
      }

      setRefetchTable(newUid(20));
      document.body.style.overflow = 'unset';
      setIsOpenAddModal(false);
    } catch (error) {
      setServerError(getConflictField(error) || 'saveFailed');
    } finally {
      setIsSaving(false);
    }
  };

  const handleError = (el) => (el ? false : checkValid ? true : false);

  return (
    <div className="bg-white">
      <div className="fixed inset-0 z-[100] flex rounded-xl bg-white/75 px-3">
        <div className="mx-auto my-8 h-max w-full max-w-xl  rounded-xl bg-white drop-shadow-m">
          <Modal.Header onClose={closeModal}>
            <h2 className="text-sm font-semibold">
              {edit
                ? t('pages.manage_competencies.tab_categories.edit_category')
                : t('pages.manage_competencies.tab_categories.new_category')}
            </h2>
          </Modal.Header>

          <Modal.Body>
            <div className="flex flex-col gap-5">
              <div>
                <h3 className="text-lg">
                  {edit
                    ? t(
                        'pages.manage_competencies.tab_categories.edit_category'
                      )
                    : t(
                        'pages.manage_competencies.tab_categories.add_category'
                      )}
                </h3>
              </div>

              <div className="flex w-full flex-col">
                <div className="mb-1 text-sm text-black">
                  <Label htmlFor="categoryName">
                    {t('pages.manage_competencies.tab_categories.name')}
                  </Label>
                  <InputText
                    id="categoryName"
                    onChange={(e) => {
                      setNameCategory(e.currentTarget.value);
                      if (serverError === 'nameConflict') setServerError('');
                    }}
                    value={nameCategory}
                    hasError={
                      handleError(nameCategory) ||
                      serverError === 'nameConflict'
                    }
                  />
                  {handleError(nameCategory) && (
                    <ErrorMessage id="errorMessage">
                      {t('pages.onboarding.error.text_fiels')}
                    </ErrorMessage>
                  )}
                  {serverError === 'nameConflict' &&
                    !handleError(nameCategory) && (
                      <span
                        role="alert"
                        className="mt-1 flex items-center text-sm text-error-04"
                      >
                        <Warning className="mr-2 inline h-5 w-5" />
                        {t(
                          'pages.manage_competencies.tab_categories.name_conflict'
                        )}
                      </span>
                    )}
                </div>
                <div className="mb-1 mt-2 max-w-[150px] text-sm text-black">
                  <Label htmlFor="codePrefix">
                    {t('pages.manage_competencies.tab_categories.code_prefix')}
                  </Label>
                  <InputText
                    id="codePrefix"
                    onChange={(e) => {
                      setCodePrefix(e.currentTarget.value);
                      if (serverError === 'codeConflict') setServerError('');
                    }}
                    value={codePrefix}
                    hasError={
                      handleError(codePrefix) || serverError === 'codeConflict'
                    }
                  />
                  {handleError(codePrefix) && (
                    <ErrorMessage id="errorMessage">
                      {t('pages.onboarding.error.text_fiels')}
                    </ErrorMessage>
                  )}
                  {serverError === 'codeConflict' &&
                    !handleError(codePrefix) && (
                      <span
                        role="alert"
                        className="mt-1 flex items-center text-sm text-error-04"
                      >
                        <Warning className="mr-2 inline h-5 w-5" />
                        {t(
                          'pages.manage_competencies.tab_categories.code_conflict'
                        )}
                      </span>
                    )}
                </div>
                <div className="mb-1 mt-2 text-sm text-black">
                  <Label htmlFor="colour">
                    {t('pages.manage_competencies.tab_categories.color')}
                  </Label>

                  <div id="colour">
                    <ColorPicker
                      colorPicker={colorPicker}
                      setColorPicker={setColorPicker}
                    />
                  </div>

                  <p className="flex items-center font-normal text-grey-06">
                    <span
                      style={{ backgroundColor: colorPicker }}
                      className={`mr-1 h-4 w-4 rounded-full`}
                    ></span>
                    {t('pages.manage_competencies.tab_categories.used_color')}
                  </p>
                </div>

                <div className="mb-1 mt-2 text-sm text-black">
                  <Label htmlFor="descriptionCat">
                    {t(
                      'pages.manage_competencies.tab_categories.short_description'
                    )}
                  </Label>
                  <InputTextarea
                    id="descriptionCat"
                    placeholder={t(
                      `pages.create_edit_course.type_course.course_description_placeholder`
                    )}
                    onChange={(ev) => setDescriptionCat(ev.target.value)}
                    value={descriptionCat}
                    limit={300}
                  />
                </div>
              </div>
              {serverError === 'saveFailed' && (
                <span
                  role="alert"
                  className="flex items-center text-sm text-error-04"
                >
                  <Warning className="mr-2 inline h-5 w-5" />
                  {t('pages.manage_competencies.tab_categories.save_error')}
                </span>
              )}
              <div className="flex justify-between">
                <Button
                  variant="ghost"
                  onClick={() => {
                    document.body.style.overflow = 'unset';
                    setIsOpenAddModal(false);
                  }}
                >
                  {t('button.cancel')}
                </Button>
                <Button
                  disabled={isSaving}
                  onClick={() => handleSaveCategory()}
                >
                  {t('pages.manage_competencies.tab_categories.save_category')}
                </Button>
              </div>
            </div>
          </Modal.Body>
        </div>
      </div>
    </div>
  );
};

export default AddCategoryModal;
